import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/AppLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { ReportMarkdownRenderer } from '@/components/ReportMarkdownRenderer';
import { 
  ArrowLeft, 
  Loader2, 
  FileText, 
  Clock,
  CheckCircle,
  AlertCircle,
  Trash2,
  Eye,
  Building2,
  Briefcase,
  User,
  Gift,
  RefreshCw,
  Plus
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface SolutionAssetReport {
  id: string;
  company_name: string | null;
  job_title: string | null;
  hiring_manager_name: string | null;
  solution_asset: string | null;
  gift_type: string | null;
  gift_content: string | null;
  proof_story_used: string | null;
  positioning_statement: string | null;
  word_count: number | null;
  status: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
}

export default function SolutionAssetReports() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [isLoading, setIsLoading] = useState(true);
  const [reports, setReports] = useState<SolutionAssetReport[]>([]);
  const [selectedReport, setSelectedReport] = useState<SolutionAssetReport | null>(null);

  const fetchReports = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('solution_asset_reports')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setReports(data || []);
      
      // Auto-select first completed report if none selected
      if (!selectedReport && data && data.length > 0) {
        const completed = data.find(r => r.status === 'completed');
        if (completed) setSelectedReport(completed);
        else setSelectedReport(data[0]);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast({
        title: "Error",
        description: "Failed to load your Solution Asset reports.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    
    // Poll for updates on pending reports
    const interval = setInterval(() => {
      const hasPending = reports.some(r => r.status === 'pending' || r.status === 'processing');
      if (hasPending) {
        fetchReports();
      }
    }, 10000); // Check every 10 seconds
    
    return () => clearInterval(interval);
  }, [user]);

  const handleDelete = async (reportId: string) => {
    try {
      const { error } = await supabase
        .from('solution_asset_reports')
        .delete()
        .eq('id', reportId);
      
      if (error) throw error;
      
      setReports(prev => prev.filter(r => r.id !== reportId));
      if (selectedReport?.id === reportId) {
        setSelectedReport(reports.find(r => r.id !== reportId) || null);
      }
      
      toast({
        title: "Report Deleted",
        description: "The Solution Asset report has been deleted.",
      });
    } catch (error) {
      console.error('Error deleting report:', error);
      toast({
        title: "Error",
        description: "Failed to delete the report.",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'pending':
      case 'processing':
        return <Badge className="bg-yellow-500"><Clock className="w-3 h-3 mr-1" />Processing</Badge>;
      case 'error':
        return <Badge variant="destructive"><AlertCircle className="w-3 h-3 mr-1" />Error</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <AppLayout currentPage="solution-asset-reports">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/')}
                className="h-9 w-9 p-0"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Solution Asset Reports</h1>
                <p className="text-muted-foreground">Your personalized consulting-style assets</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={fetchReports}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={() => navigate('/solution-asset')}>
                <Plus className="w-4 h-4 mr-2" />
                New Solution Asset
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : reports.length === 0 ? (
            <Card className="text-center py-16">
              <CardContent>
                <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">No Solution Assets Yet</h2>
                <p className="text-muted-foreground mb-6">
                  Create your first Solution Asset to stand out from other applicants.
                </p>
                <Button onClick={() => navigate('/solution-asset')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Solution Asset
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Reports List */}
              <div className="lg:col-span-1">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Your Reports ({reports.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[600px]">
                      <div className="space-y-1 p-4">
                        {reports.map((report) => (
                          <div
                            key={report.id}
                            onClick={() => setSelectedReport(report)}
                            className={`p-4 rounded-lg cursor-pointer transition-all ${
                              selectedReport?.id === report.id
                                ? 'bg-primary/10 border-2 border-primary'
                                : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="font-medium text-foreground line-clamp-1">
                                {report.job_title || 'Unknown Position'}
                              </div>
                              {getStatusBadge(report.status)}
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {report.company_name || 'Unknown Company'}
                            </div>
                            {report.gift_type && (
                              <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                <Gift className="w-3 h-3" />
                                {report.gift_type}
                              </div>
                            )}
                            <div className="text-xs text-muted-foreground mt-2">
                              {new Date(report.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>

              {/* Report Detail */}
              <div className="lg:col-span-2">
                {selectedReport ? (
                  <Card>
                    <CardHeader className="flex flex-row items-start justify-between">
                      <div>
                        <CardTitle className="text-xl flex items-center gap-2">
                          <Briefcase className="w-5 h-5 text-primary" />
                          {selectedReport.job_title || 'Solution Asset'}
                        </CardTitle>
                        <div className="text-muted-foreground flex items-center gap-4 mt-1">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {selectedReport.company_name || 'Unknown'}
                          </span>
                          {selectedReport.hiring_manager_name && (
                            <span className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {selectedReport.hiring_manager_name}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(selectedReport.status)}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" className="text-destructive">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Report?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This action cannot be undone. This will permanently delete your Solution Asset report.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(selectedReport.id)}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {selectedReport.status === 'pending' || selectedReport.status === 'processing' ? (
                        <div className="text-center py-12">
                          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                          <h3 className="text-lg font-semibold mb-2">Generating Your Solution Asset...</h3>
                          <p className="text-muted-foreground">
                            This typically takes 2-5 minutes. The page will update automatically.
                          </p>
                        </div>
                      ) : selectedReport.status === 'error' ? (
                        <div className="text-center py-12">
                          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
                          <h3 className="text-lg font-semibold mb-2">Generation Failed</h3>
                          <p className="text-muted-foreground mb-4">
                            {selectedReport.error || 'An error occurred during generation.'}
                          </p>
                          <Button onClick={() => navigate('/solution-asset')}>
                            Try Again
                          </Button>
                        </div>
                      ) : (
                        <ScrollArea className="h-[600px] pr-4">
                          <div className="space-y-6">
                            {/* Positioning Statement */}
                            {selectedReport.positioning_statement && (
                              <div className="p-4 bg-primary/10 rounded-lg border border-primary/30">
                                <h3 className="font-semibold text-primary mb-2 flex items-center gap-2">
                                  <Eye className="w-4 h-4" />
                                  Your "Only I" Statement
                                </h3>
                                <p className="text-foreground italic">
                                  "{selectedReport.positioning_statement}"
                                </p>
                              </div>
                            )}

                            {/* Main Solution Asset */}
                            {selectedReport.solution_asset && (
                              <div>
                                <h3 className="font-semibold mb-3 flex items-center gap-2">
                                  <FileText className="w-4 h-4 text-primary" />
                                  Solution Asset Brief
                                </h3>
                                <div className="prose prose-sm max-w-none">
                                  <ReportMarkdownRenderer content={selectedReport.solution_asset} />
                                </div>
                              </div>
                            )}

                            {/* Gift Content */}
                            {selectedReport.gift_content && (
                              <div className="mt-8 pt-6 border-t">
                                <h3 className="font-semibold mb-3 flex items-center gap-2">
                                  <Gift className="w-4 h-4 text-primary" />
                                  Your Gift: {selectedReport.gift_type || 'Resource'}
                                </h3>
                                <div className="prose prose-sm max-w-none bg-accent/5 p-4 rounded-lg">
                                  <ReportMarkdownRenderer content={selectedReport.gift_content} />
                                </div>
                              </div>
                            )}

                            {/* Proof Story */}
                            {selectedReport.proof_story_used && (
                              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                                <h3 className="font-semibold mb-2 text-sm text-muted-foreground">
                                  Proof Story Used
                                </h3>
                                <p className="text-foreground">{selectedReport.proof_story_used}</p>
                              </div>
                            )}

                            {/* Word Count */}
                            {selectedReport.word_count && (
                              <div className="text-sm text-muted-foreground">
                                Word count: {selectedReport.word_count.toLocaleString()}
                              </div>
                            )}
                          </div>
                        </ScrollArea>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="h-[700px] flex items-center justify-center">
                    <div className="text-center">
                      <Eye className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Select a report to view details</p>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
